/**
 * Campaigns seen on this link — which campaigns its follows actually carried.
 *
 * A link and a campaign are many-to-many (one URL in two ads and a YouTube
 * description; one campaign pointing at several URLs), so the association is
 * never stored on the link. It is read per follow from the tags the edge
 * recorded, and set beside the campaign the link declares — with a note when
 * follows came tagged with another one, which is "one URL, one campaign" not
 * being kept. Backend `admin/content/linkCampaigns.ts`.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DataTable, SearchFilterBar, type ColumnDef, type FilterDef, type SortState } from '@shared-ui/components';
import { publishedLinksApi, type LinkCampaignRow } from '../api/publishedLinksApi';

const PERIOD_FILTER: FilterDef[] = [{
  key: 'days', kind: 'select', placeholder: 'Period', required: true,
  options: [7, 30, 90, 180].map(d => ({ value: String(d), label: `Last ${d} days` })),
}];

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The first of a list, and how many more there are. */
function FirstOf({ items }: { items: string[] }) {
  if (items.length === 0) return <span className="pl-sub">—</span>;
  return (
    <span title={items.join(', ')}>
      {items[0]}{items.length > 1 && <span className="pl-sub"> +{items.length - 1}</span>}
    </span>
  );
}

export default function LinkCampaigns({ slug }: { slug: string }) {
  const [filters, setFilters] = useState<Record<string, string>>({ days: '30' });
  const days = Number(filters.days) || 30;
  const [sort, setSort] = useState<SortState>({ sortBy: 'follows', sortOrder: 'desc' });
  const { data, isLoading, error } = useQuery({
    queryKey: ['link-campaigns', slug, days],
    queryFn: () => publishedLinksApi.campaigns(slug, days),
  });

  const columns: ColumnDef<LinkCampaignRow>[] = [
    { key: 'campaign', header: 'Campaign', sortable: true, sortValue: r => r.campaign,
      render: r => (r.campaign === '(none)'
        ? <span className="pl-sub" title="Follows that carried no utm_campaign — the link's own label applies to them">No campaign tag</span>
        : <span>
            {r.campaign}
            {data?.declared && r.campaign === data.declared && <span className="pl-sub"> · declared</span>}
          </span>) },
    { key: 'follows', header: 'Follows', sortable: true, className: 'pl-num', sortValue: r => r.follows,
      render: r => r.follows.toLocaleString() },
    { key: 'landed', header: <span title="Follows whose landing page loaded and claimed the arrival">Landed</span>,
      sortable: true, className: 'pl-num', sortValue: r => r.landed, render: r => r.landed.toLocaleString() },
    { key: 'sources', header: 'Source / medium', render: r => <FirstOf items={r.sources} /> },
    { key: 'variants', header: <span title="utm_content — usually the ad">Ad</span>, render: r => <FirstOf items={r.variants} /> },
    { key: 'last', header: 'Last followed', sortable: true, sortValue: r => r.lastFollowedAt,
      render: r => <span title={r.lastFollowedAt}>{fmtWhen(r.lastFollowedAt)}</span> },
  ];

  const other = data?.otherCampaignFollows ?? 0;
  return (
    <section className="pl-dead">
      <header className="pl-dead-head">
        <h2>Campaigns seen on this link</h2>
        <p className="pl-sub">
          The campaign each follow carried in its URL tags, counted from the follows themselves — a link can sit in
          several ads and posts at once, so this is what happened rather than what the link is labelled.
          {data?.declared
            ? <> This link is labelled <strong>{data.declared}</strong>.</>
            : <> This link declares no campaign.</>}
        </p>
      </header>
      {other > 0 && data && (
        <p className="pl-warn">
          {other.toLocaleString()} of {data.follows.toLocaleString()} follows came tagged with a campaign other than
          “{data.declared}” — this URL is in use beyond the campaign it is labelled with.
        </p>
      )}
      <SearchFilterBar filters={PERIOD_FILTER} values={filters} onChange={setFilters} hideSearch hideFetch />
      {error
        ? <p className="pl-error">{(error as Error).message}</p>
        : (
          <DataTable<LinkCampaignRow>
            columns={columns}
            data={data?.rows ?? []}
            rowKey={r => r.campaign}
            sort={sort}
            onSortChange={setSort}
            loading={isLoading}
            compact
            emptyMessage={`Nobody followed this link in the last ${days} days.`}
          />
        )}
    </section>
  );
}
